<template>
    <div class="edit-flyout">
        <form class="inner">
            <header class="header">
                <button type="button" class="back-caret" @click.prevent="close" aria-label="Close">
                    <SvgsBackCaret />
                </button>
                <h2 class="subhead-2">Create My Itinerary</h2>
            </header>
            <div class="fields">
                <fieldset class="form-field">
                    <label for="itineraryName">
                        <h3 class="card-header">Itinerary Name</h3>
                        <p class="required">Required</p>
                    </label>
                    <!-- Errors are tied to the field so they are read with it. -->
                    <input
                        v-model="itineraryName"
                        type="text"
                        id="itineraryName"
                        :aria-invalid="characterLimitWarning || duplicateItinerary ? 'true' : undefined"
                        :aria-describedby="[characterLimitWarning && lengthErrorId, duplicateItinerary && duplicateErrorId].filter(Boolean).join(' ') || undefined" />
                    <p v-show="characterLimitWarning" :id="lengthErrorId" class="required red">Name must be between 3 and 40 characters.</p>
                    <p v-show="duplicateItinerary" :id="duplicateErrorId" class="required red">You already have an itinerary with this name.</p>
                </fieldset>
            </div>
            <div class="apply-button-wrapper">
                <button type="submit" @click.prevent="handleClick" class="create-button small-heavy uppercase" :disabled="!applyActive" :class="{ active: applyActive }">Create Itinerary</button>

                <button type="button" @click.prevent="close" class="cancel-button small-heavy uppercase">Cancel</button>
            </div>
        </form>
    </div>
</template>

<script setup>
    import { useUserStore } from '@/stores/user';

    const { $event } = useNuxtApp();
    const { getOhcList, updateOhcList } = useUtils();
    const applyActive = ref(false);
    const itineraryName = ref('');
    const ohcItineraries = ref([]);
    const duplicateItinerary = ref(false);
    const characterLimitWarning = ref(false);
    const lengthErrorId = useId();
    const duplicateErrorId = useId();
    const userStore = useUserStore();

    const props = defineProps({
        site: {
            type: Object,
            required: true,
            default: {},
        },
    });

    const { site } = toRefs(props);

    const emit = defineEmits(['closeFlyout']);
    function close() {
        emit('closeFlyout');
    }
    defineExpose({
        close,
    });

    function closeWithName() {
        $event('createdItineraryWithSite', {
            itineraryName: itineraryName.value,
            siteName: site.value.fields.Site,
        });

        close();
    }

    watch(itineraryName, (newVal, oldVal) => {
        duplicateItinerary.value = false;
        characterLimitWarning.value = false;

        if (itineraryName.value.length) {
            applyActive.value = true;
        } else {
            applyActive.value = false;
        }
    });

    onMounted(() => {
        if (import.meta.client) {
            const { ohcList } = getOhcList('ohcItineraries');
            ohcItineraries.value = ohcList;
        }
    });

    function addItinerary(clonedList) {
        clonedList.push({
            name: itineraryName.value,
            sites: [site.value.id],
        });

        // This is the same user action as the store's addSiteToItinerary()
        // new-itinerary branch — a site added to an itinerary that did not
        // exist yet — but it writes the list directly rather than going
        // through the store, so it never reached that action's tracking.
        // Creating your first itinerary is the common way into the feature,
        // so without this the only itinerary_updated events came from users
        // who already had one.
        useAnalytics().track('itinerary_updated', { action: 'add', size: 1 });

        updateOhcList('ohcItineraries', clonedList);
        console.log(`🍤 ~ addItinerary ~ clonedList:`, clonedList);
        userStore.refreshData();
        closeWithName();
    }

    function handleClick() {
        const { ohcList, clone } = getOhcList('ohcItineraries');
        ohcItineraries.value = ohcList;

        if (itineraryName.value && itineraryName.value.length >= 3 && itineraryName.value.length <= 40) {
            if (ohcItineraries.value && ohcItineraries.value.length) {
                const found = ohcItineraries.value.find((singleItinerary) => singleItinerary.name === itineraryName.value);
                if (found) {
                    duplicateItinerary.value = true;
                    return;
                } else {
                    addItinerary(clone);
                }
            } else {
                addItinerary(clone);
            }
        } else {
            characterLimitWarning.value = true;
        }
        userStore.refreshData();
    }
</script>

<style lang="scss" scoped>
    .edit-flyout {
        .header {
            position: relative;
            text-align: center;
            margin-bottom: 34px;

            .reset {
                position: absolute;
                right: 0;
                top: 2px;
            }

            .back-caret {
                position: absolute;
                left: 0;
                top: -3px;
            }
        }

        .form-field {
            label {
                display: flex;
                align-items: flex-end;
                margin-bottom: 10px;

                h3 {
                    margin-right: 10px;
                }
            }

            input {
                margin-bottom: 5px;
            }
        }

        .apply-button-wrapper {
            padding: 20px 0;
            background: $white;
            width: 100%;

            .create-button,
            .cancel-button {
                width: 100%;
                padding: 12px;
                font-family: $avenir;
                text-align: center;

                @media (min-width: $tablet) {
                    border: 1px solid;
                    border-radius: 3.333em;
                    padding: 1em 2em;
                    transition: 0.25s all;
                }
            }

            .create-button {
                margin-bottom: 20px;
                background-color: $black;
                color: $white;

                &:hover {
                    background-color: $white;
                    color: $black;
                }
            }

            .cancel-button {
                background-color: $gray-light;
                border: 1px solid $gray-common;
                color: $black;

                &:hover {
                    background-color: $black;
                    color: $gray-light;
                }
            }
        }
    }
</style>
